import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Smartphone, Mail, Clock, Loader2 } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal } from "@/components/shared/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitContact, formatApiError } from "@/lib/api";
import { COMPANY } from "@/data/site";

const empty = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Message sent!");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: MapPin, label: "Office Address", value: COMPANY.address },
    { icon: Smartphone, label: "Mobile", value: COMPANY.mobiles.join(", "), href: `tel:${COMPANY.mobiles[0]}` },
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  ];

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent("Sector 9, Gurgaon, Haryana 122001")}&output=embed`;

  return (
    <div data-testid="contact-page">
      <PageHero
        breadcrumb="Contact"
        eyebrow="Get in touch"
        title="Let's talk about your move."
        description="Questions, quotes or support — our team is here to help, seven days a week."
      />

      <section className="bg-white py-24 lg:py-32">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          {/* Details */}
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Reach us" title="Contact information." />
            <div className="mt-8 space-y-5">
              {details.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex gap-4 rounded-2xl border border-gray-100 bg-surface p-5" data-testid={`contact-${label.split(" ")[0].toLowerCase()}`}>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand text-white"><Icon className="h-5 w-5" /></div>
                  <div>
                    <p className="text-sm font-semibold text-ink">{label}</p>
                    {href ? (
                      <a href={href} className="text-muted-foreground hover:text-brand transition-colors break-words">{value}</a>
                    ) : (
                      <p className="text-muted-foreground">{value}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex gap-4 rounded-2xl border border-gray-100 bg-surface p-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand text-white"><Clock className="h-5 w-5" /></div>
                <div>
                  <p className="text-sm font-semibold text-ink">Working Hours</p>
                  <p className="text-muted-foreground">Mon – Sun: 8:00 AM – 9:00 PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <Reveal direction="left" className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="rounded-3xl border border-gray-100 bg-surface p-6 sm:p-10 shadow-card" data-testid="contact-form">
              <h3 className="font-heading font-bold text-2xl text-ink mb-6">Send us a message</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="c-name">Name *</Label>
                  <Input id="c-name" data-testid="contact-name" value={form.name} onChange={set("name")} className="bg-white h-12 rounded-xl" placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="bg-white h-12 rounded-xl" placeholder="you@example.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="bg-white h-12 rounded-xl" placeholder="Mobile number" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-subject">Subject</Label>
                  <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} className="bg-white h-12 rounded-xl" placeholder="How can we help?" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="c-message">Message *</Label>
                  <Textarea id="c-message" data-testid="contact-message" value={form.message} onChange={set("message")} className="bg-white rounded-xl min-h-[130px]" placeholder="Write your message..." />
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-8 py-4 hover:bg-brand-deep transition-colors disabled:opacity-70">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24 lg:pb-32">
        <div className="container-x">
          <Reveal className="rounded-3xl overflow-hidden border border-gray-100 shadow-card">
            <iframe
              title="Agarwal Secure Packers & Movers location"
              src={mapSrc}
              className="w-full h-[420px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
